import { normalizeAdvisorCode } from "@/lib/advisorCode";
import { formatLoanId, resolveClientInitials } from "@/lib/loanId";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export type ScenarioStatus = "draft" | "saved" | "committed";

export type ScenarioRow = {
  id: string;
  user_id: string;
  name: string | null;
  loan_id: string | null;
  is_draft: boolean;
  status: ScenarioStatus;
  committed_at: string | null;
  data: Json;
  created_at: string;
  updated_at: string;
};

export async function listScenarios(): Promise<ScenarioRow[]> {
  const { data, error } = await supabase
    .from("scenarios")
    .select("*")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return data as ScenarioRow[];
}

export async function getScenario(id: string): Promise<ScenarioRow | null> {
  const { data, error } = await supabase.from("scenarios").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return (data as ScenarioRow) ?? null;
}

export async function upsertScenario(
  userId: string,
  payload: {
    id?: string | null;
    name?: string | null;
    loan_id?: string | null;
    is_draft: boolean;
    status?: ScenarioStatus;
    data: Json;
  },
): Promise<ScenarioRow> {
  if (payload.id) {
    const updatePayload: {
      name: string | null;
      loan_id: string | null;
      is_draft: boolean;
      data: Json;
      status?: ScenarioStatus;
    } = {
      name: payload.name ?? null,
      loan_id: payload.loan_id ?? null,
      is_draft: payload.is_draft,
      data: payload.data,
    };
    if (payload.status) updatePayload.status = payload.status;
    const { data, error } = await supabase
      .from("scenarios")
      .update(updatePayload)
      .eq("id", payload.id)
      .select()
      .single();
    if (error) throw error;
    return data as ScenarioRow;
  }
  const insertPayload: {
    user_id: string;
    name: string | null;
    loan_id: string | null;
    is_draft: boolean;
    data: Json;
    status?: ScenarioStatus;
  } = {
    user_id: userId,
    name: payload.name ?? null,
    loan_id: payload.loan_id ?? null,
    is_draft: payload.is_draft,
    data: payload.data,
  };
  if (payload.status) insertPayload.status = payload.status;
  const { data, error } = await supabase
    .from("scenarios")
    .insert(insertPayload)
    .select()
    .single();
  if (error) throw error;
  return data as ScenarioRow;
}

export async function deleteScenario(id: string): Promise<void> {
  const { error } = await supabase.from("scenarios").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Promote a scenario to `committed` (Commitment Agreement generated). Idempotent.
 *
 * Server-authoritative: the transition runs through the `commit-scenario`
 * edge function — ownership and the one-way transition are enforced there,
 * the `enforce_commitment_limit` trigger still blocks a second free
 * commitment, and `protect_scenario_status` blocks direct client status
 * writes. Errors throw with the server's code in the message so
 * `isCommitmentLimitError` keeps working.
 */
export async function markScenarioCommitted(id: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke("commit-scenario", {
    body: { scenario_id: id },
  });
  if (error) {
    // FunctionsHttpError carries the response; surface the server's code.
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      const body = await ctx.json().catch(() => null) as
        | { error?: { code?: string; message?: string } }
        | null;
      const code = body?.error?.code;
      if (code) throw new Error(code);
    }
    throw new Error(error.message || "commit-failed");
  }
  const payload = data as { ok?: boolean; error?: string } | null;
  if (!payload?.ok) throw new Error(payload?.error || "commit-failed");
}

/** Reserve next sequential loan number for the signed-in user. */
export async function reserveServerLoanSeq(userId: string): Promise<number> {
  const { data, error } = await supabase.rpc("next_loan_seq", { _user_id: userId });
  if (error) throw error;
  return data as number;
}

/** Consent wording shown to a client before an advisor is granted read access. */
export const ADVISOR_CONSENT_VERSION = "v1";
export const ADVISOR_CONSENT_TEXT =
  "I give this advisor read-only access to my saved Family Loan Planner™ scenarios. " +
  "My family remains the owner of this data, and I can revoke this access at any time.";

export type AdvisorPreview = {
  advisor_user_id: string;
  display_name: string | null;
};

/**
 * Resolve a code to the advisor it belongs to WITHOUT granting anything.
 *
 * The client confirms this name before access is given, so a mistyped code
 * that happens to be another live advisor's cannot silently hand them the
 * family's scenarios. Shares the link RPC's failure counter and lockout.
 */
export async function previewAdvisorByCode(code: string): Promise<AdvisorPreview | null> {
  const { data, error } = await supabase.rpc("preview_advisor_by_code", {
    _code: normalizeAdvisorCode(code),
  });
  if (error) throw error;
  const rows = (data ?? []) as AdvisorPreview[];
  return rows.length > 0 ? rows[0] : null;
}

export async function linkAdvisorByCode(code: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("link_advisor_by_code", {
    _code: normalizeAdvisorCode(code),
    _consent_text: ADVISOR_CONSENT_TEXT,
    _consent_version: ADVISOR_CONSENT_VERSION,
  });
  if (error) throw error;
  return Boolean(data);
}

export type MyAdvisorLink = {
  advisor_user_id: string;
  linked_via_code: string | null;
  granted_at: string;
  revoked_at: string | null;
  consent_version: string;
  consent_text: string | null;
  advisor_email?: string | null;
};

/** Advisors currently (or previously) connected to the signed-in client. */
export async function listMyAdvisors(clientUserId: string): Promise<MyAdvisorLink[]> {
  const { data, error } = await supabase
    .from("advisor_clients")
    .select("advisor_user_id, linked_via_code, granted_at, revoked_at, consent_version, consent_text")
    .eq("client_user_id", clientUserId)
    .order("granted_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as MyAdvisorLink[];
}

/** Client-controlled revoke. Revoked advisors immediately lose access. */
export async function revokeAdvisorAccess(advisorUserId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("revoke_advisor_access", { _advisor: advisorUserId });
  if (error) throw error;
  return Boolean(data);
}
/**
 * Build a unique copy name: "Name (Copy)", then "Name (Copy 2)", ...
 * `existingNames` should include every current scenario name for the user,
 * including the source's own name.
 */
export function copyScenarioName(sourceName: string, existingNames: string[]): string {
  const taken = new Set(existingNames.map((n) => n.trim().toLowerCase()));
  const base = sourceName.trim() || "Untitled scenario";
  const first = `${base} (Copy)`;
  if (!taken.has(first.toLowerCase())) return first;
  for (let n = 2; ; n++) {
    const candidate = `${base} (Copy ${n})`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/**
 * Duplicate a scenario under the given owner.
 *
 * The copy always starts as a Draft with a freshly reserved Loan ID, even
 * when the source is Committed — so it never counts toward the free plan's
 * one-commitment limit and never carries over a signed agreement. Payment
 * tracking history lives in a separate table keyed by scenario id, so it is
 * naturally not copied.
 */
export async function duplicateScenario(
  userId: string,
  source: ScenarioRow,
  existingNames: string[],
): Promise<ScenarioRow> {
  const data = (source.data ?? {}) as Record<string, unknown>;
  const cloned = JSON.parse(JSON.stringify(data)) as Record<string, unknown>;

  const seq = await reserveServerLoanSeq(userId);
  const initials = resolveClientInitials(
    (cloned.loanBorrowerName as string) || "",
    (cloned.policyOwnerName as string) || "",
  );
  const loanId = formatLoanId(initials, seq);

  return upsertScenario(userId, {
    id: null,
    name: copyScenarioName(source.name, existingNames),
    loan_id: loanId,
    is_draft: true,
    status: "draft",
    data: cloned as ScenarioRow["data"],
  });
}
